import React, { useState } from 'react';
import '../../styles/filterAnimations.css';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  Badge,
  Fab,
  useTheme,
  useMediaQuery,
  Slide,
  Button,
  Stack,
  Divider
} from '@mui/material';
import {
  Tune as TuneIcon,
  Close as CloseIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import AdvancedFiltersStructured from './AdvancedFiltersStructured';
import SaveSearchModal from './SaveSearchModal';
import SavedSearchDropdown from './SavedSearchDropdown';
import CategorySearchSimulator from './CategorySearchSimulator';

/**
 * 📱 SIDEBAR MOBILE-OPTIMISÉ AVEC ANIMATIONS
 * 
 * Fonctionnalités:
 * ✅ Design responsive adaptatif
 * ✅ Animations fluides d'ouverture/fermeture
 * ✅ FAB flottant avec badge de notifications
 * ✅ Geste swipe pour fermer (mobile)
 * ✅ Sauvegarde des recherches
 * ✅ Chargement des recherches sauvegardées
 */
const ResponsiveFilterSidebar = ({ onFiltersChange, onNotification }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isOpen, setIsOpen] = useState(false);
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  const [currentFilters, setCurrentFilters] = useState({});
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [externalFilters, setExternalFilters] = useState(null);

  // 🔄 GESTION DE L'OUVERTURE/FERMETURE
  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  // 📊 GESTION DES CHANGEMENTS DE FILTRES
  const handleFiltersChange = (filters) => {
    // Utiliser le comptage passé par AdvancedFiltersStructured si disponible
    const count = filters._activeCount || 0;
    
    setActiveFiltersCount(count);
    setCurrentFilters(filters); // Sauvegarder les filtres actuels
    
    // Propager les changements
    onFiltersChange?.(filters);
  };

  // 💾 GESTION DE LA SAUVEGARDE
  const handleSaveSearch = () => {
    if (activeFiltersCount > 0) {
      setSaveModalOpen(true);
    }
  };

  const handleSearchSaved = (savedSearchData) => {
    console.log('✅ [ResponsiveFilterSidebar] Search saved:', savedSearchData);
    onNotification?.('Recherche sauvegardée avec succès', 'success');
  };

  // 🔄 CHARGEMENT D'UNE RECHERCHE SAUVEGARDÉE
  const handleApplySavedSearch = (filters, searchData) => {
    console.log('🔄 [ResponsiveFilterSidebar] Applying saved search:', searchData);
    console.log('🔄 [ResponsiveFilterSidebar] Filters to apply:', filters);
    
    // Passer les filtres au composant AdvancedFiltersStructured
    setExternalFilters(filters);
    
    // Appliquer les filtres
    handleFiltersChange(filters);
    
    onNotification?.(`Recherche "${searchData.name}" appliquée`, 'success');
  };

  const handleSavedSearchError = (error) => {
    console.error('❌ [ResponsiveFilterSidebar] Saved search error:', error);
    onNotification?.(error, 'error');
  };

  // 🎨 ANIMATIONS
  const fabVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: { 
      scale: 1, 
      rotate: 0,
      transition: { type: "spring", stiffness: 260, damping: 20 }
    },
    tap: { scale: 0.9 }
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 0.5 }
  };

  return (
    <>
      {/* � SIMULATION RECHERCHE PAR CATEGORIE */}
      <CategorySearchSimulator
        onApplySearch={handleApplySavedSearch}
        onError={handleSavedSearchError}
      />
      
      {/* �🚀 FAB FLOTTANT */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            variants={fabVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            whileTap="tap"
            style={{
              position: 'fixed',
              bottom: isMobile ? 300 : 200,
              right: 24,
              zIndex: theme.zIndex.speedDial
            }}
          >
            <Badge 
              badgeContent={activeFiltersCount} 
              color="secondary"
              max={9}
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.7rem',
                  minWidth: '20px',
                  height: '20px',
                  zIndex: theme.zIndex.speedDial + 1, // ✅ Z-index plus élevé que le FAB
                  background: 'linear-gradient(45deg, #ff6b6b, #ee5a24)',
                  animation: activeFiltersCount > 0 ? 'pulse 2s infinite' : 'none',
                  boxShadow: '0 4px 12px rgba(255, 107, 107, 0.4)',
                  fontWeight: 'bold',
                  border: '2px solid white', // ✅ Bordure blanche pour contraste
                  '@keyframes pulse': {
                    '0%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(255, 107, 107, 0.7)' },
                    '70%': { transform: 'scale(1.1)', boxShadow: '0 0 0 10px rgba(255, 107, 107, 0)' },
                    '100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(255, 107, 107, 0)' }
                  }
                }
              }}
            >
              <Fab
                color="primary"
                onClick={toggleSidebar}
                className={`filter-fab-pulse filter-button-hover ${activeFiltersCount > 0 ? 'filter-selected' : ''}`}
                sx={{
                  background: 'linear-gradient(45deg, #6200ee 30%, #4b00b5 90%)',
                  boxShadow: '0 8px 32px rgba(98, 0, 238, 0.4)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative', // ✅ Position relative pour bien positionner le badge
                  zIndex: theme.zIndex.speedDial, // ✅ Z-index explicite mais plus bas que le badge
                  '&:hover': {
                    background: 'linear-gradient(45deg, #7c4dff 30%, #6200ee 90%)',
                    boxShadow: '0 12px 40px rgba(98, 0, 238, 0.6)',
                    transform: 'translateY(-3px) scale(1.05)'
                  }
                }}
              >
                <motion.div
                  animate={{ 
                    rotate: isOpen ? 45 : 0,
                    scale: activeFiltersCount > 0 ? [1, 1.1, 1] : 1
                  }}
                  transition={{ 
                    duration: 0.3,
                    scale: { duration: 0.6, repeat: activeFiltersCount > 0 ? Infinity : 0, repeatDelay: 2 }
                  }}
                >
                  <TuneIcon />
                </motion.div>
              </Fab>
            </Badge>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 📱 DRAWER/SIDEBAR */}
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={toggleSidebar}
        variant={isMobile ? "temporary" : "temporary"}
        ModalProps={{
          keepMounted: true, // Meilleure performance mobile
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: isMobile ? '100%' : 420,
            maxWidth: '100vw',
            height: '100%',
            border: 'none',
            borderRadius: isMobile ? 0 : '20px 0 0 20px',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0,0,0,0.3)',
            backdropFilter: 'blur(4px)'
          }
        }}
        SlideProps={{
          direction: "left"
        }}
      >
        <motion.div
          initial={{ x: 300, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 300, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          {/* 🎯 EN-TÊTE AVEC FERMETURE */}
          <Box
            sx={{
              p: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
              background: 'linear-gradient(45deg, #6200ee 30%, #4b00b5 90%);',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TuneIcon sx={{ mr: 2, fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('filters.title', 'Filtres de Recherche')}
              </Typography>
            </Box>

            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <IconButton 
                onClick={toggleSidebar}
                sx={{ 
                  color: 'white',
                  background: 'rgba(255,255,255,0.1)',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.2)'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </motion.div>
          </Box>

          {/* 📋 SECTION RECHERCHES SAUVEGARDÉES */}
          {user && (
            <Box sx={{ 
              p: 2, 
              borderTop: `1px solid ${theme.palette.divider}`,
              background: 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(10px)'
            }}>
              <Stack spacing={2}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#6200ee' }}>
                  📋 {t('savedSearches.title', 'Recherches sauvegardées')}
                </Typography>
                
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  {/* 🔄 Charger recherches */}
                  <SavedSearchDropdown
                    onApplySearch={handleApplySavedSearch}
                    onError={handleSavedSearchError}
                  />
                  
                  {/* 💾 Sauvegarder recherche actuelle */}
                  <Button
                    onClick={handleSaveSearch}
                    disabled={activeFiltersCount === 0}
                    startIcon={<SaveIcon />}
                    variant="contained"
                    size="small"
                    sx={{
                      background: activeFiltersCount > 0 
                        ? 'linear-gradient(45deg, #6200ee 30%, #4b00b5 90%)'
                        : 'rgba(0, 0, 0, 0.12)',
                      '&:hover': {
                        background: activeFiltersCount > 0 
                          ? 'linear-gradient(45deg, #7c4dff 30%, #6200ee 90%)'
                          : 'rgba(0, 0, 0, 0.12)'
                      },
                      '&:disabled': {
                        color: 'rgba(0, 0, 0, 0.26)'
                      }
                    }}
                  >
                    {t('savedSearches.save', 'Sauvegarder')}
                  </Button>
                </Stack>
                
                {activeFiltersCount === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {t('savedSearches.selectFiltersFirst', 'Sélectionnez des filtres pour pouvoir sauvegarder votre recherche')}
                  </Typography>
                )}
              </Stack>
            </Box>
          )}

          {/* 📋 CONTENU DES FILTRES */}
          <Box sx={{ flex: 1, overflow: 'auto', pb: 2 }}>
            <AdvancedFiltersStructured
              onFiltersChange={handleFiltersChange}
              onNotification={onNotification}
              externalFilters={externalFilters}
              onFiltersApplied={() => {
                setExternalFilters(null);
                setIsOpen(false);
              }}
            />
          </Box>
        </motion.div>
      </Drawer>

      {/* 💾 MODAL DE SAUVEGARDE */}
      <SaveSearchModal
        open={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        filters={currentFilters}
        onSaved={handleSearchSaved}
      />
    </>
  );
};

export default ResponsiveFilterSidebar;
